import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import {
  listProcesses,
  type ListProcessesParams,
  type ProcessListResponse,
} from '@api/processes';

const PROCESSES_QUERY_KEY_ROOT = ['processes'] as const;

/**
 * Per-page size used by the gallery's infinite-scroll. Kept in the cache key
 * so that changing the page size (e.g. on a wider viewport) starts a fresh
 * cache rather than mixing different page shapes.
 */
export const DEFAULT_PROCESSES_PAGE_SIZE = 20;

/** Filters that participate in the cache key — `page` is intentionally omitted because every page lives under the same key. */
export type ProcessesListFilters = Omit<ListProcessesParams, 'page'>;

/**
 * Compose the React Query cache key for the processes list. Other hooks (e.g.
 * mutations that need to invalidate the list) import this so we never have a
 * stringly-typed mismatch.
 *
 * NOTE: `page` is deliberately not part of the key — `useInfiniteQuery`
 * stores all pages under the same key as a `{ pages, pageParams }` object.
 */
export const processesQueryKey = (
  params: ProcessesListFilters,
): readonly unknown[] => [
  ...PROCESSES_QUERY_KEY_ROOT,
  'list',
  {
    status: params.status ?? 'all',
    search: params.search ?? '',
    sort: params.sort ?? 'newest',
    size: params.size ?? DEFAULT_PROCESSES_PAGE_SIZE,
  },
] as const;

/**
 * The "any list" key — pass to `queryClient.invalidateQueries({ queryKey:
 * processesListRootKey })` after a mutation to refetch every active
 * filter combination at once.
 */
export const processesListRootKey = [
  ...PROCESSES_QUERY_KEY_ROOT,
  'list',
] as const;

/**
 * Infinite gallery query. Pages are loaded incrementally via
 * `fetchNextPage()` (typically wired to an IntersectionObserver sentinel at
 * the bottom of the list). The cache value is `InfiniteData<…, number>` —
 * `data.pages` is an array of `ProcessListResponse`s, one per fetched page,
 * and `pageParams[i]` is the `page` param that produced `pages[i]`.
 */
export function useProcesses(
  params: ProcessesListFilters,
): UseInfiniteQueryResult<InfiniteData<ProcessListResponse, number>, Error> {
  const size = params.size ?? DEFAULT_PROCESSES_PAGE_SIZE;
  return useInfiniteQuery<
    ProcessListResponse,
    Error,
    InfiniteData<ProcessListResponse, number>,
    readonly unknown[],
    number
  >({
    queryKey: processesQueryKey(params),
    queryFn: ({ pageParam }) => listProcesses({ ...params, page: pageParam, size }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const next = lastPage.page + 1;
      return next < lastPage.totalPages ? next : undefined;
    },
  });
}
